import { FC } from 'react';

import { buildSanityImageUrl } from '@lib/sanity/image';
import type { SanityTrustItem } from '@lib/sanity/types';

type AsSeenOnStripProps = {
  items?: SanityTrustItem[];
  label?: string;
  /** Render logos greyscale + dimmed. Off by default (full-colour). */
  monochrome?: boolean;
  /** Logo height in px. Defaults to 15. */
  logoHeight?: number;
};

const DEFAULT_LOGO_HEIGHT = 10;

/**
 * Static "As seen on" proof strip for the email gate (spec 6.5). Renders
 * real logo marks where Sanity provides them, falling back to the outlet
 * name as styled text while logo assets are still placeholders — matching
 * the v2 mock, which shows Sky Sports / The Athletic / Telegraph as text.
 */
const AsSeenOnStrip: FC<AsSeenOnStripProps> = ({
  items,
  label = 'As seen on',
  monochrome = false,
  logoHeight
}) => {
  if (!items || items.length === 0) return null;

  // `||` (not a default param): Sanity returns null for unset fields.
  const resolvedLogoHeight = logoHeight || DEFAULT_LOGO_HEIGHT;

  return (
    <div>
      <p className="mb-3 text-center text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-neutral-400 font-body">
        {label}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
        {items.map((item) => {
          if (item.logo?.asset) {
            return (
              <img
                key={item._key}
                src={buildSanityImageUrl(item.logo)
                  .height(resolvedLogoHeight * 2)
                  .url()}
                alt={item.altText}
                className={`object-contain ${
                  monochrome ? 'opacity-60 grayscale' : ''
                }`}
                style={{ height: `${resolvedLogoHeight}px` }}
              />
            );
          }

          return (
            <span
              key={item._key}
              className="font-body text-sm font-bold italic text-neutral-400"
            >
              {item.altText}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default AsSeenOnStrip;
